import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Fade from 'react-reveal/Fade';

const theme = {
  textInverse:    '#f7f8fa',
  border:         '#010606',
  borderHover:    '#000000',
  buttonBg:       '#45FE47',
  buttonText:     '#010606',
  buttonHoverBg:  '#010606',
  buttonHoverText:'#f7f8fa',
};

const sectionConfig = {
  id: 'react',
  isLight: false,
  topLine: 'Creation Lab',
  headline: 'Practical AI, Built Human-First',
  description:
    'A living portfolio of tools and experiments: learning aids, archetype systems, and PWAs you can try or explore.',
  buttonLabel: 'Browse portfolio',
  buttonTo: '/templates',
};

const CreationLabSection = ({ embedded = false }) => {
  const { id, isLight, topLine, headline, description, buttonLabel, buttonTo } = sectionConfig;

  return (
    <Section $isLight={isLight} id={id} $embedded={embedded}>
      <Container $embedded={embedded}>
        <Content $embedded={embedded}>
          <Fade bottom cascade damping={0.2}>
            <TopLine>{topLine}</TopLine>
            <Heading $embedded={embedded}>{headline}</Heading>
            <Subtitle $embedded={embedded}>{description}</Subtitle>

            <ButtonWrap data-cta-wrap="true">
              <Button to={buttonTo}>
                {buttonLabel}
              </Button>
            </ButtonWrap>
          </Fade>
        </Content>
      </Container>
    </Section>
  );
};

const Section = styled.section`
  background: transparent;
  border-top: none;
  border-bottom: ${({ $embedded }) => ($embedded ? 'none' : '0.8px solid #010606')};
  height: ${({ $embedded }) => ($embedded ? '100%' : 'auto')};

  @media (max-width: 768px) {
    border-top: 1px solid #45FE47;
    height: auto;
  }
`;

const Container = styled.div`
  padding: ${({ $embedded }) => ($embedded ? '56px 36px 56px' : '48px 24px 0')};
  margin: 0 auto;
  max-width: ${({ $embedded }) => ($embedded ? 'none' : '1200px')};
  display: grid;
  justify-content: ${({ $embedded }) => ($embedded ? 'stretch' : 'center')};
  background: ${({ $embedded }) =>
    $embedded
      ? 'transparent'
      : 'linear-gradient(135deg, rgba(15, 15, 15, 0.9), rgba(30, 30, 30, 0.8))'};
  backdrop-filter: ${({ $embedded }) => ($embedded ? 'none' : 'blur(10px)')};
  border-radius: ${({ $embedded }) => ($embedded ? '0' : '0 0 12px 12px')};
  border-bottom: ${({ $embedded }) => ($embedded ? 'none' : '1px solid #45FE47')};
  border-top: none;
  border-left: ${({ $embedded }) => ($embedded ? 'none' : '1px solid #45FE47')};
  border-right: ${({ $embedded }) => ($embedded ? 'none' : '1px solid #45FE47')};
  color: #fff;
  height: ${({ $embedded }) => ($embedded ? '100%' : 'auto')};
  box-sizing: border-box;

  @media (max-width: 1200px) {
    margin: ${({ $embedded }) => ($embedded ? '0' : '0 25px')};
  }

  @media (max-width: 768px) {
    padding: 60px 20px 0;
    border-radius: 0;
    border-bottom: none;
    border-top: none;
    border-left: none;
    border-right: none;
    background: #45FE47;
    color: #010606;
    backdrop-filter: none;
    margin: 0;
    height: auto;
    justify-content: center;
  }
`;

const Content = styled.div`
  max-width: ${({ $embedded }) => ($embedded ? 'none' : '800px')};
  width: 100%;
  padding-bottom: ${({ $embedded }) => ($embedded ? '0' : '60px')};
  height: ${({ $embedded }) => ($embedded ? '100%' : 'auto')};

  @media (max-width: 768px) {
    padding-bottom: 40px;
    height: auto;
  }
`;

const TopLine = styled.p`
  font-family: 'VT323', monospace;
  color: #45fe47;
  font-size: 16px;
  line-height: 16px;
  font-weight: 700;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  margin: 0 0 16px;

  @media (max-width: 768px) {
    color: #000;
  }
`;

const Heading = styled.h2`
  font-family: 'VT323', monospace;
  color: ${theme.textInverse};
  font-size: ${({ $embedded }) => ($embedded ? '36px' : '48px')};
  line-height: 1.1;
  font-weight: 600;
  margin: 0 0 20px;

  @media (max-width: 768px) {
    color: #000;
    font-size: 30px;
    line-height: 1.15;
    margin-bottom: 16px;
  }

  @media (max-width: 480px) {
    font-size: 28px;
  }
`;

const Subtitle = styled.p`
  font-family: ${({ $embedded }) =>
    $embedded ? '"Encode Sans Expanded", sans-serif' : '"VT323", monospace'};
  color: ${theme.textInverse};
  font-size: ${({ $embedded }) => ($embedded ? '16px' : '24px')};
  line-height: ${({ $embedded }) => ($embedded ? '1.55' : '1.2')};
  margin: 0 0 28px;

  @media (max-width: 768px) {
    font-family: 'Encode Sans Expanded', sans-serif;
    color: #010606;
    font-size: 15px;
    line-height: 1.55;
    margin-bottom: 18px;
  }
`;

const ButtonWrap = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const Button = styled(Link)`
  font-family: 'VT323', monospace;
  background: ${theme.buttonBg};
  color: ${theme.buttonText};
  border: 1px solid ${theme.border};
  padding: 12px 32px;
  font-size: 18px;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.buttonHoverBg};
    color: ${theme.buttonHoverText};
    border-color: ${theme.borderHover};
  }
`;

export default CreationLabSection;
